// Ancoragem de preço: compara o nosso preço ao consumidor com o de um produto âncora da
// concorrência. Regras puras (sem banco) — a meta é a "elasticidade" definida pelo Marketing.
import { gramaturaKg } from './rampaIndicadores';

/**
 * Margem do varejo sobre o PREÇO DE GÔNDOLA (PCO). O PCO é estimado "de trás para frente" a partir do
 * nosso preço médio de venda: PCO = preço médio ÷ (1 − margem). Definido pela gestão:
 *  - preço CHEIO: o varejo fica com 28% → PCO = preço médio ÷ 0,72 (é o usado no painel);
 *  - preço PROMOCIONAL: 22% → ÷ 0,78 (hoje não é exibido).
 * Para mudar, altere aqui.
 */
export const MARGEM_VAREJO_CHEIO_PCT = 28;
export const MARGEM_VAREJO_PROMO_PCT = 22;

/** Legenda para o time entender de onde vem o PCO (aparece na tela e nos relatórios). */
export function legendaPco(en = false): string {
  const d = (1 - MARGEM_VAREJO_CHEIO_PCT / 100).toFixed(2).replace('.', en ? '.' : ',');
  return en
    ? `PCO (consumer shelf price) achieved = average sales price ÷ ${d} (the retailer keeps ${MARGEM_VAREJO_CHEIO_PCT}% of the shelf price), converted to units by the pack size. Suggested PCO = anchor product price × target elasticity. Gap = achieved ÷ suggested − 1: below suggestion (red) = pricing too low.`
    : `PCO realizado = preço médio de venda ÷ ${d} (o varejo fica com ${MARGEM_VAREJO_CHEIO_PCT}% do preço de gôndola), convertido em unidade pela gramatura. PCO sugerido = preço do produto âncora × elasticidade. Gap = realizado ÷ sugerido − 1: abaixo da sugestão (vermelho) = precificando abaixo.`;
}

export interface Ancora {
  nome: string | null;
  gramatura: string | null; // do âncora; vazio = mesma do nosso produto
  elasticidade: number | null; // % do preço do âncora = PISO da meta (90 = no mínimo 90% do âncora; 108 = no mínimo 108%)
  base: 'un' | 'kg'; // a elasticidade vale por unidade ou por kg (só importa se as gramaturas diferem)
}

export interface AncoraMes {
  nossoUn: number; // R$/un. ao consumidor
  nossoKg: number;
  ancoraUn: number;
  ancoraKg: number;
  pctUn: number; // nosso ÷ âncora, em %
  pctKg: number;
  pct: number; // o que vale, conforme a base escolhida
  metaPct: number | null;
  alvoUn: number | null; // PCO SUGERIDO por unidade = âncora × elasticidade
  gapPct: number | null; // PCO realizado ÷ sugerido − 1, em % (negativo = precificando abaixo)
  ok: boolean | null; // cumprindo a meta? (null = sem meta)
}

/** Gramaturas diferentes entre o nosso produto e o âncora (aí é preciso escolher un × kg). */
export function gramaturasDiferem(nossa: string | null | undefined, ancora: string | null | undefined): boolean {
  const a = gramaturaKg(ancora);
  const n = gramaturaKg(nossa);
  return a != null && n != null && Math.abs(a - n) > 1e-9;
}

/**
 * Compara um preço NOSSO ao consumidor (R$/un.) com o do âncora (R$/un.).
 * Usado na Capa (PCO sugerido) e, via calcularAncora, na Rampa (preço médio + markup).
 */
export function compararPco(
  nossoUn: number | null | undefined,
  gramaturaNossa: string | null | undefined,
  a: Ancora,
  precoAncoraUn: number | null | undefined,
): AncoraMes | null {
  const gN = gramaturaKg(gramaturaNossa);
  if (gN == null || nossoUn == null || nossoUn <= 0 || precoAncoraUn == null || precoAncoraUn <= 0) return null;
  const gA = gramaturaKg(a.gramatura) ?? gN;
  const nossoKg = nossoUn / gN;
  const ancoraKg = precoAncoraUn / gA;
  const pctUn = (nossoUn / precoAncoraUn) * 100;
  const pctKg = (nossoKg / ancoraKg) * 100;
  const pct = a.base === 'kg' ? pctKg : pctUn;
  const meta = a.elasticidade;
  // A elasticidade é um PISO: abaixo dela perdemos rentabilidade (maior é melhor), esteja ela acima ou abaixo de 100%.
  const ok = meta == null ? null : pct >= meta;
  return {
    nossoUn,
    nossoKg,
    ancoraUn: precoAncoraUn,
    ancoraKg,
    pctUn,
    pctKg,
    pct,
    metaPct: meta,
    alvoUn: meta == null ? null : (precoAncoraUn * meta) / 100,
    gapPct: meta == null || meta <= 0 ? null : (pct / meta - 1) * 100,
    ok,
  };
}

/**
 * PCO realizado = preço médio (R$/kg) ÷ (1 − margem do varejo) × gramatura (kg).
 * @param precoMedioKg preço médio de venda (R$/kg) do mês — o PRM da Rampa
 * @param precoAncoraUn preço ao consumidor do âncora no mês (R$/un.)
 */
export function calcularAncora(
  a: Ancora,
  gramaturaNossa: string | null | undefined,
  precoMedioKg: number | null | undefined,
  precoAncoraUn: number | null | undefined,
  margemVarejoPct: number = MARGEM_VAREJO_CHEIO_PCT,
): AncoraMes | null {
  const gN = gramaturaKg(gramaturaNossa);
  if (gN == null || precoMedioKg == null || precoMedioKg <= 0 || margemVarejoPct >= 100) return null;
  const nossoUn = (precoMedioKg / (1 - margemVarejoPct / 100)) * gN;
  return compararPco(nossoUn, gramaturaNossa, a, precoAncoraUn);
}

export const ROTULO_ANCORA_PCT = 'Preço vs. âncora (% do âncora)';
export const ROTULO_ANCORA_PCT_EN = 'Price vs. anchor product (% of anchor)';
